import { CustomModal } from '../../../../../common/CustomModal'
import { Button, Form, Input, Popconfirm } from 'antd'
import React, { FC, useState } from 'react'
import './index.less'
import { useCreateOrUpdatePatientMutation } from '../../../../../../core/api/patientApi'
import { DecryptedPatient } from '@icure/cardinal-sdk/model.mjs'
import { PATIENT_ID_CARD_IDENTIFIER } from '../../../../../../constants'
import { getDecryptedPropertyStub } from '../../../../../../helpers/property'

export interface ModalCreatePatientOnHubFormType {
  idCardNumber: string
  firstName: string
  lastName: string
  ssin: string
}

interface ModalCreatePatientOnHubProps {
  isVisible: boolean
  onClose: () => void
  patient: DecryptedPatient
  selectedHub: string
  onSubmit: (values: ModalCreatePatientOnHubFormType) => void
  t: (key: string, ctxt?: { [key: string]: string }) => string
}

export const ModalCreatePatientOnHub: FC<ModalCreatePatientOnHubProps> = ({ isVisible, onClose, selectedHub, onSubmit, patient, t }) => {
  const [patientForm] = Form.useForm<ModalCreatePatientOnHubFormType>()
  const [updatePatientInCardinal] = useCreateOrUpdatePatientMutation()
  const [isConfirmVisible, setIsConfirmVisible] = useState(false)

  const handleSubmitClick = async () => {
    try {
      // Validate the form first
      await patientForm.validateFields()
      // If validation passes, show the confirmation
      setIsConfirmVisible(true)
    } catch {
      // Validation failed – AntD will show errors, do not open the Popconfirm
    }
  }

  const handleConfirm = () => {
    patientForm.submit()
    const { idCardNumber, firstName, lastName, ssin } = patientForm.getFieldsValue()
    const idCardNumberProperty = getDecryptedPropertyStub(PATIENT_ID_CARD_IDENTIFIER, idCardNumber)

    const res = updatePatientInCardinal(
      new DecryptedPatient({
        ...patient,
        firstName,
        lastName,
        ssin,
        properties: [...(patient?.properties?.filter((p) => p?.type?.identifier !== PATIENT_ID_CARD_IDENTIFIER) ?? []), idCardNumberProperty],
      }),
    )
    setIsConfirmVisible(false)
  }

  const handleCancelConfirm = () => {
    patientForm.submit()
    // onClose()
    setIsConfirmVisible(false)
  }

  const getCustomFooter = () => {
    return [
      <Button key="back" onClick={onClose}>
        {t('modalUploadReportToHub.modalCreatePatientOnHub.buttons.cancel')}
      </Button>,

      <Popconfirm
        key="confirmClose"
        title={t('modalUploadReportToHub.modalCreatePatientOnHub.popconfirm.title')}
        description={
          <>
            {t('modalUploadReportToHub.modalCreatePatientOnHub.popconfirm.description.line1', {
              hub: selectedHub,
            })}
            <br />
            {t('modalUploadReportToHub.modalCreatePatientOnHub.popconfirm.description.line2')}
          </>
        }
        open={isConfirmVisible}
        onOpenChange={(visible) => {
          // We control opening ourselves via validation; only react to closing
          if (!visible) {
            setIsConfirmVisible(false)
          }
        }}
        onConfirm={handleConfirm}
        onCancel={handleCancelConfirm}
        okText={t('modalUploadReportToHub.modalCreatePatientOnHub.buttons.popconfirmOk')}
        cancelText={t('modalUploadReportToHub.modalCreatePatientOnHub.buttons.popconfirmCancel')}
        placement="topRight"
      >
        <Button key="submit" type="primary" onClick={handleSubmitClick}>
          {t('modalUploadReportToHub.modalCreatePatientOnHub.buttons.submit')}
        </Button>
      </Popconfirm>,
    ]
  }

  return (
    <CustomModal
      handleClose={onClose}
      isVisible={isVisible}
      title={t('modalUploadReportToHub.modalCreatePatientOnHub.title', {
        hub: selectedHub,
      })}
      customFooter={getCustomFooter()}
    >
      <div className="modalCreatePatientOnHub">
        <Form
          className="modalCreatePatientOnHub__form"
          initialValues={{
            idCardNumber: patient?.properties?.find((p) => p?.type?.identifier === PATIENT_ID_CARD_IDENTIFIER)?.typedValue?.stringValue,
            firstName: patient.firstName,
            lastName: patient.lastName,
            ssin: patient.ssin,
          }}
          form={patientForm}
          layout="vertical"
          onFinish={(values: ModalCreatePatientOnHubFormType) => onSubmit(values)}
        >
          <Form.Item
            name="firstName"
            label={t('modalUploadReportToHub.modalCreatePatientOnHub.form.labels.firstName')}
            rules={[
              {
                required: true,
                message: t('modalUploadReportToHub.modalCreatePatientOnHub.form.validation.firstNameRequired'),
              },
            ]}
          >
            <Input id="firstName" placeholder={t('modalUploadReportToHub.modalCreatePatientOnHub.form.placeholders.firstName')} />
          </Form.Item>

          <Form.Item
            name="lastName"
            label={t('modalUploadReportToHub.modalCreatePatientOnHub.form.labels.lastName')}
            rules={[
              {
                required: true,
                message: t('modalUploadReportToHub.modalCreatePatientOnHub.form.validation.lastNameRequired'),
              },
            ]}
          >
            <Input id="lastName" placeholder={t('modalUploadReportToHub.modalCreatePatientOnHub.form.placeholders.lastName')} />
          </Form.Item>

          <Form.Item
            name="ssin"
            label={t('modalUploadReportToHub.modalCreatePatientOnHub.form.labels.ssin')}
            rules={[
              {
                required: true,
                message: t('modalUploadReportToHub.modalCreatePatientOnHub.form.validation.ssinRequired'),
              },
            ]}
          >
            <Input id="ssin" placeholder={t('modalUploadReportToHub.modalCreatePatientOnHub.form.placeholders.ssin')} />
          </Form.Item>

          <Form.Item
            name="idCardNumber"
            label={t('modalUploadReportToHub.modalCreatePatientOnHub.form.labels.idCardNumber')}
            rules={[
              {
                required: true,
                message: t('modalUploadReportToHub.modalCreatePatientOnHub.form.validation.idCardNumberRequired'),
              },
            ]}
          >
            <Input id="idCardNumber" placeholder={t('modalUploadReportToHub.modalCreatePatientOnHub.form.placeholders.idCardNumber')} />
          </Form.Item>
        </Form>
      </div>
    </CustomModal>
  )
}
